import { Component, Input, OnChanges, SimpleChange, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular';
import { ToastService, ToastType } from '../../toast/toast.service';
import { ChangeLogAction, ChangeLogItem, ChangeLogItemDisplay } from '../models/change-log-item';
import { ChangeLogService } from '../services/change-log.service';
import { DxoColumnFixingModule } from 'devextreme-angular/ui/nested';

@Component({
    selector: 'app-change-log-viewer-details',
    templateUrl: './change-log-viewer-details.component.html',
    styleUrls: ['./change-log-viewer-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxDataGridModule, DxoColumnFixingModule]
})
export class ChangeLogViewerDetailsComponent implements OnChanges {
  @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
  @Input() item: ChangeLogItem;
  public itemDisplay: ChangeLogItemDisplay = null;

  dontCompareColumns = ['UpdatedDate', 'UpdatedBy'];

  constructor(
    private toastService: ToastService,
    private changeLogService: ChangeLogService) { }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const itemChange: SimpleChange = changes.item;
    if (itemChange) {
      this.itemDisplay = this.itemDisplayFromItem(itemChange.currentValue);
      if (!await this.findPrevious()) {
        this.dataGrid.instance.refresh();
      }
    }
  }

  itemDisplayFromItem(item: ChangeLogItem): ChangeLogItemDisplay {
    const obj = JSON.parse(item.JsonLog);

    // Don't add extra information to obj until after the columns are created
    const columns = this.calculateColumns(obj);

    this.updateForeignKeyValues(obj);

    // The changeLogId is used to find previous change; when null the previous menu item is hidden
    obj.changeLogId = (item.LogAction === ChangeLogAction.Update) ? item.Id : null;

    return { ...item, columns, rows: [obj] };
  }

  calculateColumns(obj: any): any[] {
    const res: any[] = Object.entries(obj)
      .map(([e]) => e)
      .filter(e => !(e.startsWith('@') || e.startsWith('$')))
      .map(e => ({ dataField: e, width: 'auto', columnAutoWidth: true, allowSorting: false }));

    res.push({
      type: 'buttons',
      width: 'auto',
      columnAutoWidth: true,
      allowSorting: false,
      buttons: [
        {
          text: 'Previous',
          hint: 'Find previous change',
          visible: (e: any) => this.canFindPrevious(e?.row?.data),
          onClick: (e: any) => this.onFindPrevious(),
        }
      ],
    });

    return res;
  }

  updateForeignKeyValues(obj: any) {
    const foreignKeys: any[] = Object.entries(obj)
      .map(([e]) => e)
      .filter(e => e.startsWith('@'))
      .map(e => e.slice(1));

    for (const key of foreignKeys) {
      const value = obj[key] === null ? '' : obj[key];
      const id = obj['@' + key] === null ? '' : obj['@' + key];
      obj[key] = value + (id === '' ? '' : ` (${id})`);
    }
  }

  canFindPrevious(current: any): boolean {
    return this.itemDisplay && current.changeLogId;
  }

  async onFindPrevious(): Promise<void> {
    if (!await this.findPrevious()) {
      this.dataGrid.instance.refresh();
      this.toastService.showToast('No previous change found', ToastType.info);
    }
  }

  async findPrevious(): Promise<any> {
    let item: any = null;
    if (this.itemDisplay) {
      const lastItem = this.itemDisplay.rows.slice(-1)[0];
      if (this.canFindPrevious(lastItem)) {
        item = await this.changeLogService.getPreviousLogItem(lastItem.changeLogId);
        if (item !== null) {
          const obj = JSON.parse(item.JsonLog);
          this.updateForeignKeyValues(obj);
          obj.changeLogId = item.Id;
          this.itemDisplay.rows.push(obj);
        }

        // Remove changeLogId; no need to try again, also removes menu item
        lastItem.changeLogId = null;
      }
    }
    return item;
  }

  onCellPrepared(ev) {
    if (this.itemDisplay && (ev.rowType === 'data')) {
      const previousRow = this.getRowData(ev.rowIndex + 1);
      if (previousRow) {
        const currentRow = ev.data;
        const columnName: string = ev.column.dataField;

        if (this.dontCompareColumns.find(e => e === columnName) === undefined) {
          let className: string;

          if (previousRow[columnName]) {
            if ((currentRow[columnName] === undefined) || (currentRow[columnName] === null) || (currentRow[columnName] === '')) {
              className = 'changeDelete';
            }
            else if (previousRow[columnName] !== currentRow[columnName]) {
              className = 'changeUpdate';
            }
          }
          else {
            if (currentRow[columnName]) {
              className = 'changeAdd';
            }
          }

          if (className) {
            ev.cellElement[0].classList.add(className);
          }
        }
      }
    }
  }

  getRowData(index: number): any {
    return (index >= 0) && (index < this.itemDisplay.rows.length) ? this.itemDisplay.rows[index] : null;
  }
}
